import { useEffect, useRef, useCallback, useMemo } from 'react';

export interface ProgressUpdate {
    type: 'connected' | 'progress' | 'complete';
    requestId?: string;
    count?: number;
    total?: number;
    progress?: number;
    timestamp?: number;
}

interface UseBulkRatingProgressOptions {
    requestId: string;
    totalCount: number;
    onProgress: (update: ProgressUpdate) => void;
    enabled?: boolean;
}

export function useBulkRatingProgress({ requestId, totalCount, onProgress, enabled = true }: UseBulkRatingProgressOptions) {
    const eventSourceRef = useRef<EventSource | null>(null);
    const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isCompletedRef = useRef(false);
    const onProgressRef = useRef(onProgress);

    // Keep the callback ref updated without triggering reconnects
    useEffect(() => {
        onProgressRef.current = onProgress;
    }, [onProgress]);

    // Stable key for connection identity
    const connectionKey = useMemo(() => 
        enabled && requestId && totalCount ? `${requestId}:${totalCount}` : null,
        [enabled, requestId, totalCount]
    );

    useEffect(() => {
        if (!connectionKey) return;

        // Reset completed flag for new connection
        isCompletedRef.current = false;

        // Close any existing connection
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
        }
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }

        const connect = () => {
            if (isCompletedRef.current) return; // Don't reconnect if completed
            
            const eventSource = new EventSource(`/api/bulk-rating/progress?requestId=${encodeURIComponent(requestId)}&totalCount=${totalCount}`);
            eventSourceRef.current = eventSource;

            eventSource.onopen = () => {
                console.log('SSE: Connection established');
            };

            eventSource.onmessage = (event) => {
                try {
                    const data: ProgressUpdate = JSON.parse(event.data);
                    console.log('SSE: Received message', data);
                    onProgressRef.current(data);
                    
                    // Mark as completed if progress is 100 or type is complete
                    if (data.type === 'complete' || data.progress === 100 || (data.count !== undefined && data.total !== undefined && data.count >= data.total)) {
                        isCompletedRef.current = true;
                        eventSource.close();
                        eventSourceRef.current = null;
                        if (reconnectTimeoutRef.current) {
                            clearTimeout(reconnectTimeoutRef.current);
                            reconnectTimeoutRef.current = null;
                        }
                    }
                } catch (err) {
                    console.error('SSE: Failed to parse message:', err);
                }
            };

            eventSource.onerror = () => {
                eventSource.close();
                eventSourceRef.current = null;

                // Only reconnect if not completed
                if (!isCompletedRef.current) {
                    console.error('SSE: Connection error, will retry...');
                    reconnectTimeoutRef.current = setTimeout(() => {
                        console.log('SSE: Attempting to reconnect...');
                        connect();
                    }, 3000);
                }
            };
        };

        connect();

        return () => {
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
                eventSourceRef.current = null;
            }
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
        };
    }, [connectionKey, requestId, totalCount]);

    const disconnect = useCallback(() => {
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
        }
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }
    }, []);

    return { disconnect };
}
